import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { authStore } from '../../stores/authStore';
import { themeStore } from '../../stores/themeStore';
import { companyConfig } from '../../config/company';
import { useProfilePhoto } from '../../hooks/useProfilePhoto';
import FloatingChat from '../Chat/FloatingChat';
import {
  Menu, X, ChevronDown, Sun, Moon, LogOut, User,
  Home, Users, FileText, Wrench, HeadphonesIcon,
  Package, CreditCard, TrendingUp, Settings,
  Wifi, Server, MapPin, Building, UserCheck,
  DollarSign, ClipboardList, AlertCircle
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = authStore();
  const { theme, toggleTheme } = themeStore();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useProfilePhoto();

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const isPublicContractPage = location.pathname && location.pathname.startsWith('/assinacontrato');
  if (isPublicContractPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-6xl mx-auto p-4">
          <Outlet />
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      title: 'Dashboard',
      icon: Home,
      path: '/',
      permission: null, // Todos têm acesso
    },
    {
      title: 'Vendas',
      icon: TrendingUp,
      children: [
        { 
          title: 'Clientes', 
          path: '/clientes', 
          permission: { module: 'clientes', action: 'view' } 
        },
        { 
          title: 'Contratos', 
          path: '/contratos', 
          permission: { module: 'contratos', action: 'view' } 
        },
      ],
    },
    {
      title: 'Operacional',
      icon: Wrench,
      children: [
        { 
          title: 'Atendimentos', 
          path: '/atendimentos', 
          permission: { module: 'atendimentos', action: 'view' } 
        },
        { 
          title: 'Chamados', 
          path: '/chamados', 
          permission: { module: 'chamados', action: 'view' } 
        },
        { 
          title: 'Ordens de Serviço', 
          path: '/ordens-servico', 
          permission: { module: 'ordens_servico', action: 'view' } 
        },
      ],
    },
    {
      title: 'Infraestrutura',
      icon: Server,
      children: [
        { 
          title: 'Fibra Ótica', 
          children: [
            { 
              title: 'OLTs', 
              path: '/olts', 
              permission: { module: 'olts', action: 'view' } 
            },
            { 
              title: 'Slots OLT', 
              path: '/slots', 
              permission: { module: 'slots', action: 'view' } 
            },
            { 
              title: 'PONs', 
              path: '/pons', 
              permission: { module: 'pons', action: 'view' } 
            },
            { 
              title: 'Caixas de Emenda', 
              path: '/caixas-emenda', 
              permission: { module: 'caixas_emenda', action: 'view' } 
            },
            { 
              title: 'Caixas de Distribuição', 
              path: '/caixas-distribuicao', 
              permission: { module: 'caixas_distribuicao', action: 'view' } 
            },
            { 
              title: 'Fibras de Emenda', 
              path: '/fibras-emenda', 
              permission: { module: 'fibras_emenda', action: 'view' } 
            },
            { 
              title: 'Splitters', 
              path: '/splitters', 
              permission: { module: 'splitters', action: 'view' } 
            },
            { 
              title: 'Cabos de Fibra', 
              path: '/cabos-fibra', 
              permission: { module: 'cabos_fibra', action: 'view' } 
            },
            { 
              title: 'Tubos Loose', 
              path: '/tubos-loose', 
              permission: { module: 'tubos_loose', action: 'view' } 
            },
            { 
              title: 'Fibras de Cabo', 
              path: '/fibras-cabo', 
              permission: { module: 'fibras_cabo', action: 'view' } 
            },
            { 
              title: 'Caixas', 
              path: '/caixas', 
              permission: { module: 'caixas', action: 'view' } 
            },
          ]
        },
        { 
          title: 'Estações', 
          path: '/estacoes', 
          permission: { module: 'estacoes', action: 'view' } 
        },
        { 
          title: 'IPs', 
          path: '/ips', 
          permission: { module: 'ips', action: 'view' } 
        },
        { 
          title: 'VLANs', 
          path: '/vlans', 
          permission: { module: 'vlans', action: 'view' } 
        },
        { 
          title: 'Mapa de Rede', 
          path: '/mapa-infra', 
          permission: { module: 'mapa_infra', action: 'view' } 
        } 
      ],
    },
    {
      title: 'Estoque',
      icon: Package,
      children: [
        { 
          title: 'Tipos de Equipamentos', 
          path: '/tipos-equipamento', 
          permission: { module: 'tipos_equipamento', action: 'view' } 
        },
        { 
          title: 'Equipamentos/Inventário', 
          path: '/equipamentos', 
          permission: { module: 'equipamentos', action: 'view' } 
        },
        { 
          title: 'Fabricantes', 
          path: '/fabricantes', 
          permission: { module: 'fabricantes', action: 'view' } 
        },
        { 
          title: 'Transferência em Lote', 
          path: '/transferencias', 
          permission: { module: 'transferencias', action: 'view' } 
        },
      ],
    },
    {
      title: 'Financeiro',
      icon: CreditCard,
      children: [
        { 
          title: 'Movimentações', 
          path: '/movimentacoes', 
          permission: { module: 'movimentacoes', action: 'view' } 
        },
        { 
          title: 'Boletos', 
          path: '/boletos', 
          permission: { module: 'boletos', action: 'view' } 
        }
      ],
    },
    {
      title: 'Administrativo',
      icon: Settings,
      children: [
        { 
          title: 'Usuários', 
          path: '/usuarios', 
          permission: { module: 'usuarios', action: 'view' } 
        },
        { 
          title: 'Cargos', 
          path: '/cargos', 
          permission: { module: 'cargos', action: 'view' } 
        },
        { 
          title: 'Planos', 
          path: '/planos', 
          permission: { module: 'planos', action: 'view' } 
        },
        { 
          title: 'Servicos Adicionais', 
          path: '/servicos-adicionais', 
          permission: { module: 'servicos_adicionais', action: 'view' } 
        },
        { 
          title: 'Vouchers', 
          path: '/vouchers', 
          permission: { module: 'vouchers', action: 'view' } 
        },
        { 
          title: 'Promoções', 
          path: '/promocoes', 
          permission: { module: 'promocoes', action: 'view' } 
        },
        { 
          title: 'Cidades', 
          path: '/cidades', 
          permission: { module: 'cidades', action: 'view' } 
        },
        { 
          title: 'Empresas', 
          path: '/empresas', 
          permission: { module: 'empresas', action: 'view' } 
        },
        { 
          title: 'Logs', 
          path: '/logs', 
          permission: { module: 'logs', action: 'view' } 
        },
        { 
          title: 'Relatórios', 
          path: '/relatorios', 
          permission: { module: 'relatorios', action: 'view' } 
        },
        { 
          title: 'Tipo de Atendimento', 
          path: '/tipos-atendimento', 
          permission: { module: 'tipos_atendimento', action: 'view' } 
        },
        { 
          title: 'Tipo de Chamado', 
          path: '/tipos-chamado', 
          permission: { module: 'tipos_chamado', action: 'view' } 
        },
        { 
          title: 'Tipo de OS', 
          path: '/tipos-os', 
          permission: { module: 'tipos_os', action: 'view' } 
        },
        { 
          title: 'Categoria Financeira', 
          path: '/categorias-financeiras', 
          permission: { module: 'categorias_financeiras', action: 'view' } 
        },
        { 
          title: 'Contas Bancárias', 
          path: '/contas-bancarias', 
          permission: { module: 'contas_bancarias', action: 'view' } 
        }
        ,{ 
          title: 'Módulos do Sistema', 
          path: '/modulos-sistema', 
          permission: { module: 'modulos_sistema', action: 'view' } 
        }
        ,{ 
          title: 'Ações do Sistema', 
          path: '/acoes', 
          permission: { module: 'acoes', action: 'view' } 
        }
      ],
    },
  ];

  const filterMenuItems = (items) => {
    return items.filter(item => {
      if (!item.permission) return true;
      return hasPermission(item.permission.module, item.permission.action);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Sidebar Desktop */}
      <aside className={`fixed left-0 top-0 z-40 h-screen transition-transform ${
        sidebarOpen ? 'w-64' : 'w-20'
      } bg-white dark:bg-dark-card border-r border-gray-200 dark:border-dark-border hidden lg:block`}>
        
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-dark-border">
          <div 
            className={`flex items-center ${!sidebarOpen ? 'w-full justify-center cursor-pointer' : 'cursor-pointer'}`}
            onClick={() => !sidebarOpen && setSidebarOpen(true)}
            title={!sidebarOpen ? 'Clique para expandir o menu' : ''}
          >
            <img 
              src={companyConfig.logo} 
              alt={companyConfig.name} 
              className={`h-8 object-contain ${sidebarOpen ? 'mr-3 max-w-[120px]' : 'max-w-[40px]'} transition-all duration-300`}
            />
            {sidebarOpen && (
              <span className="text-lg font-semibold text-gray-800 dark:text-dark-text whitespace-nowrap">
                {companyConfig.name}
              </span>
            )}
          </div>
          {sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
            >
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          )}
          {!sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="absolute bottom-4 left-1/2 transform -translate-x-1/2 p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          )}
        </div>

        {/* User Profile Section - Social Media Style */}
        {sidebarOpen && (
          <div className="px-4 py-6 border-b border-gray-200 dark:border-dark-border">
            <button
              onClick={() => navigate('/profile')}
              className="w-full flex flex-col items-center space-y-3 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
            >
              {/* Large Profile Photo */}
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200 dark:border-gray-600 group-hover:border-blue-500 transition-colors">
                <img
                  src={user?.fotoPerfil || companyConfig.profile.defaultProfilePhoto}
                  alt={user?.nome || 'Usuário'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-primary-500 flex items-center justify-center" style={{ display: 'none' }}>
                  <span className="text-white text-xl font-medium">
                    {user?.nome?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              </div>
              
              {/* User Info */}
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {user?.nome}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.cargo?.nome}
                </p>
              </div>
            </button>
          </div>
        )}

        {/* Collapsed User Profile */}
        {!sidebarOpen && (
          <div className="px-2 py-4 border-b border-gray-200 dark:border-dark-border">
            <button
              onClick={() => navigate('/profile')}
              className="w-full flex justify-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
              title={`Ver perfil de ${user?.nome}`}
            >
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200 dark:border-gray-600 group-hover:border-blue-500 transition-colors">
                <img
                  src={user?.fotoPerfil || companyConfig.profile.defaultProfilePhoto}
                  alt={user?.nome || 'Usuário'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-primary-500 flex items-center justify-center" style={{ display: 'none' }}>
                  <span className="text-white text-sm font-medium">
                    {user?.nome?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Menu Items */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-20rem)] scrollbar-hide">
          {menuItems.map((item, index) => (
            <MenuItemComponent 
              key={index} 
              item={item} 
              sidebarOpen={sidebarOpen}
              filterMenuItems={filterMenuItems}
              onExpandSidebar={() => setSidebarOpen(true)}
            />
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className={`transition-all ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Top Bar */}
        <header className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center justify-between h-16 px-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4 ml-auto">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5 text-gray-600" />
                ) : (
                  <Sun className="w-5 h-5 text-yellow-500" />
                )}
              </button>

              {/* User Menu */}
              <div className="flex items-center space-x-3">
                {/* User Profile Photo - Clickable */}
                <button
                  onClick={() => navigate('/profile')}
                  className="w-12 h-12 rounded-full overflow-hidden border-2 border-transparent hover:border-blue-500 transition-colors cursor-pointer"
                  title="Ver meu perfil"
                >
                  <img
                    src={user?.fotoPerfil || companyConfig.profile.defaultProfilePhoto}
                    alt={user?.nome || 'Usuário'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Se falhar ao carregar, mostra a inicial do nome
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-primary-500 flex items-center justify-center" style={{ display: 'none' }}>
                    <span className="text-white text-sm font-medium">
                      {user?.nome?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                </button>

                {/* User Dropdown Menu */}
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-medium text-gray-700 dark:text-dark-text">
                        {user?.nome}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-dark-muted">
                        {user?.cargo?.nome}
                      </p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </DropdownMenu.Trigger>

                <DropdownMenu.Portal>
                  <DropdownMenu.Content className="bg-white dark:bg-dark-card rounded-lg shadow-lg p-2 min-w-[200px] z-50">
                    <DropdownMenu.Separator className="h-px bg-gray-200 dark:bg-dark-border my-1" />
                    <DropdownMenu.Item 
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-red-600"
                      onClick={handleLogout}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sair</span>
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50">
            <div className="fixed left-0 top-0 h-full w-64 bg-white dark:bg-dark-card shadow-xl">
              <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-dark-border">
                <div className="flex items-center">
                  <img 
                    src={companyConfig.logo} 
                    alt={companyConfig.name} 
                    className="h-8 object-contain mr-3 max-w-[80px]" 
                  />
                  <span className="text-lg font-semibold text-gray-800 dark:text-dark-text whitespace-nowrap">
                    {companyConfig.name}
                  </span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Mobile User Profile Section */}
              <div className="px-4 py-6 border-b border-gray-200 dark:border-dark-border">
                <button
                  onClick={() => {
                    navigate('/profile');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {/* Mobile Profile Photo */}
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200 dark:border-gray-600">
                    <img
                      src={user?.fotoPerfil || companyConfig.profile.defaultProfilePhoto}
                      alt={user?.nome || 'Usuário'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-primary-500 flex items-center justify-center" style={{ display: 'none' }}>
                      <span className="text-white text-sm font-medium">
                        {user?.nome?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Mobile User Info */}
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {user?.nome}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user?.cargo?.nome}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      Ver perfil →
                    </p>
                  </div>
                </button>
              </div>
              
              <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-14rem)] scrollbar-hide-mobile">
                {menuItems.map((item, index) => (
                  <MenuItemComponent 
                    key={index} 
                    item={item} 
                    sidebarOpen={true}
                    filterMenuItems={filterMenuItems}
                    onNavigate={() => setMobileMenuOpen(false)}
                  />
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Floating Chat - Disponível em todas as páginas */}
      <FloatingChat />
    </div>
  );
};

// Função utilitária para verificar se existe um item ativo no menu
const hasActiveChild = (item, currentPath) => {
  if (item.path && currentPath === item.path) return true;
  if (item.children) {
    return item.children.some(child => hasActiveChild(child, currentPath));
  }
  return false;
};

// Componente de Item do Menu
const MenuItemComponent = ({ item, sidebarOpen, filterMenuItems, onNavigate, onExpandSidebar, level = 0 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Verificar se este item ou algum filho está ativo
  const hasActiveItem = hasActiveChild(item, location.pathname);
  const [expanded, setExpanded] = useState(hasActiveItem);

  // Atualizar estado expandido quando a rota mudar
  useEffect(() => {
    setExpanded(hasActiveItem);
  }, [hasActiveItem]);

  const handleClick = () => {
    if (!sidebarOpen) {
      onExpandSidebar?.();
      return;
    }

    if (item.path) {
      navigate(item.path);
      onNavigate?.();
    } else if (item.children) {
      setExpanded(!expanded);
    }
  };

  const isActive = location.pathname === item.path;

  if (item.children) {
    const filteredChildren = filterMenuItems(item.children);
    if (filteredChildren.length === 0) return null;

    const marginLeft = level > 0 ? `ml-${level * 4}` : '';
    const paddingLeft = level > 0 ? `pl-${(level + 1) * 4}` : '';

    return (
      <div className={marginLeft}>
        <button
          onClick={handleClick}
          className={`w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
            expanded ? 'bg-gray-100 dark:bg-gray-700' : ''
          }`}
        >
          <div className="flex items-center">
            {item.icon && <item.icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
            {sidebarOpen && (
              <span className={`${item.icon ? 'ml-3' : ''} text-sm font-medium text-gray-700 dark:text-dark-text`}>
                {item.title}
              </span>
            )}
          </div>
          {sidebarOpen && (
            <ChevronDown 
              className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform ${
                expanded ? 'rotate-180' : ''
              }`} 
            />
          )}
        </button>
        {expanded && sidebarOpen && (
          <div className="ml-4 mt-1 space-y-1">
            {filteredChildren.map((child, index) => {
              // Se o filho tem children, renderiza recursivamente
              if (child.children) {
                return (
                  <SubMenuItemComponent
                    key={index}
                    item={child}
                    sidebarOpen={sidebarOpen}
                    filterMenuItems={filterMenuItems}
                    onNavigate={onNavigate}
                    onExpandSidebar={onExpandSidebar}
                    level={level + 1}
                  />
                );
              }
              
              // Se o filho tem path, renderiza como link
              return (
                <Link
                  key={index}
                  to={child.path}
                  onClick={(e) => {
                    if (!sidebarOpen) {
                      e.preventDefault();
                      onExpandSidebar?.();
                      return;
                    }
                    onNavigate?.();
                  }}
                  className={`block p-2 pl-8 rounded-lg text-sm transition-colors ${
                    location.pathname === child.path
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {child.title}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.path}
      onClick={(e) => {
        if (!sidebarOpen) {
          e.preventDefault();
          onExpandSidebar?.();
          return;
        }
        onNavigate?.();
      }}
      className={`flex items-center p-2 rounded-lg transition-colors ${
        isActive
          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      {item.icon && <item.icon className="w-5 h-5" />}
      {sidebarOpen && (
        <span className="ml-3 text-sm font-medium">{item.title}</span>
      )}
    </Link>
  );
};

//Componente para submenu aninhado
const SubMenuItemComponent = ({ item, sidebarOpen, filterMenuItems, onNavigate, onExpandSidebar, level = 1 }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Verificar se este item ou algum filho está ativo
  const hasActiveItem = hasActiveChild(item, location.pathname);
  const [expanded, setExpanded] = useState(hasActiveItem);

  // Atualizar estado expandido quando a rota mudar
  useEffect(() => {
    setExpanded(hasActiveItem);
  }, [hasActiveItem]);

  const handleClick = () => {
    if (item.path) {
      navigate(item.path);
      onNavigate?.();
    } else if (item.children) {
      setExpanded(!expanded);
    }
  };

  const filteredChildren = item.children ? filterMenuItems(item.children) : [];
  if (item.children && filteredChildren.length === 0) return null;

  return (
    <div>
      <button
        onClick={handleClick}
        className={`w-full flex items-center justify-between p-2 pl-4 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors ${
          expanded ? 'bg-gray-50 dark:bg-gray-600' : ''
        }`}
      >
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {item.title}
        </span>
        <ChevronDown 
          className={`w-3 h-3 text-gray-600 dark:text-gray-400 transition-transform ${
            expanded ? 'rotate-180' : ''
          }`} 
        />
      </button>
      {expanded && (
        <div className="ml-4 mt-1 space-y-1">
          {filteredChildren.map((child, index) => (
            <Link
              key={index}
              to={child.path}
              onClick={(e) => {
                if (!sidebarOpen) {
                  e.preventDefault();
                  onExpandSidebar?.();
                  return;
                }
                onNavigate?.();
              }}
              className={`block p-2 pl-6 rounded-lg text-sm transition-colors ${
                location.pathname === child.path
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {child.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MainLayout;